import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity, FileText, GitBranch, CalendarDays, Clock, Receipt, CalendarOff,
  Building2, Users, BarChart2, Settings, Lock, Layers, Sun, Moon, LogOut,
  TrendingUp, Workflow, Boxes, Target, Wallet, Grid3x3, GitPullRequestArrow,
  ShieldCheck, Package, Scale, DollarSign,
} from 'lucide-react'
import { useMsal } from '@azure/msal-react'

import { useUIStore } from '@/stores/uiStore'
import type { ForgeModule } from '@/components/layout/AppShell'

const MODULES = [
  { to: '/monitoring', label: 'Monitoring',       icon: Activity,    key: 'monitoring' },
  { to: '/contracts',  label: 'Contracts',        icon: FileText,    key: 'contracts'  },
  { to: '/process',    label: 'Process',          icon: GitBranch,   key: 'process'    },
  { to: '/planning',   label: 'Project Planning', icon: CalendarDays,key: 'planning'   },
  { to: '/time',       label: 'Time Reporting',   icon: Clock,       key: 'time'       },
  { to: '/invoicing',  label: 'Invoicing',        icon: Receipt,     key: 'invoicing'  },
  { to: '/subscriptions', label: 'Subscriptions', icon: Package,     key: 'subscriptions' },
  { to: '/sales',      label: 'Sales',            icon: TrendingUp,  key: 'sales'      },
  { to: '/leads',      label: 'Leads',            icon: Target,      key: 'leads'      },
  { to: '/cost',       label: 'Azure Cost',       icon: Wallet,      key: 'cost'       },
  { to: '/devops',     label: 'DevOps',           icon: GitPullRequestArrow, key: 'devops' },
]

const WORKSPACE = [
  { to: '/customers', label: 'Customers',   icon: Building2, key: 'customers' },
  { to: '/projects',  label: 'Projects',    icon: Layers,    key: 'projects'  },
  { to: '/people',    label: 'People',      icon: Users,     key: 'people'    },
  { to: '/reports',   label: 'Reports',     icon: BarChart2, key: 'reports'   },
]

const SETTINGS_NAV = [
  { to: '/settings/modules',                 label: 'Modules',                 icon: Boxes       },
  { to: '/settings/integrations',            label: 'Integrations',            icon: Settings    },
  { to: '/settings/public-holidays',         label: 'Public Holidays',         icon: CalendarOff },
  { to: '/settings/revenue-account-matrix',  label: 'Revenue Account Matrix',  icon: Grid3x3     },
  { to: '/settings/billing',                 label: 'Billing & products',      icon: Scale       },
  { to: '/subscriptions/reports',            label: 'Subscription reports',    icon: BarChart2   },
  { to: '/subscriptions/reconciliation',     label: 'Subscription recon.',     icon: ShieldCheck },
  { to: '/settings/audit',                   label: 'Audit Dashboard',         icon: ShieldCheck },
  { to: '/settings/finance',                 label: 'Finance Settings',        icon: DollarSign  },
]

interface SidebarProps {
  user: { username?: string; name?: string; idTokenClaims?: Record<string, unknown> } | undefined
  roles: string[]
  modules: ForgeModule[]
}

export default function Sidebar({ user, roles, modules }: SidebarProps) {
  const pathname  = useRouterState({ select: (s) => s.location.pathname })
  const { sidebarCollapsed: collapsed, theme, toggleTheme } = useUIStore()
  const { instance } = useMsal()
  const isActive  = (to: string) => pathname.startsWith(to)
  const initials  = getInitials(user?.name ?? user?.username ?? 'U')
  const lowerRoles    = roles.map(r => r.toLowerCase())
  const isAdmin       = lowerRoles.includes('admin')       || import.meta.env.DEV
  const isClementine  = lowerRoles.includes('clementine')  || import.meta.env.DEV

  // Build a lookup of prod status from DB; if /modules hasn't loaded yet show all (optimistic)
  const prodKeys = modules.length > 0
    ? new Set(modules.filter(m => m.isProd).map(m => m.key))
    : null

  const visibleModules = MODULES.filter(m => {
    if (!prodKeys) return true
    if (prodKeys.has(m.key)) return true
    return isAdmin
  })

  const visibleWorkspace = WORKSPACE.filter(w => {
    if (!prodKeys) return true
    if (prodKeys.has(w.key)) return true
    return isAdmin
  })

  return (
    <aside className="sidebar">
      <div style={{ flex: 1, overflow: 'auto', paddingTop: 8 }}>

        <div className="nav-section-label">Modules</div>
        {visibleModules.map(({ to, label, icon: Icon, key }) => {
          const isDev = prodKeys && !prodKeys.has(key)
          return (
            <Link
              key={to}
              to={to}
              className={`nav-item${isActive(to) ? ' active' : ''}`}
              title={label}
            >
              <Icon size={17} className="nav-icon" />
              <span className="nav-label">{label}</span>
              {isDev && !collapsed && (
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                  color: 'var(--ns-brand)', opacity: 0.8,
                  background: 'color-mix(in oklch, var(--ns-brand) 12%, transparent)',
                  borderRadius: 3, padding: '1px 4px', marginLeft: 'auto',
                }}>DEV</span>
              )}
            </Link>
          )
        })}

        <div className="nav-section-label" style={{ marginTop: 12 }}>Workspace</div>
        {visibleWorkspace.map(({ to, label, icon: Icon, key }) => {
          const isDev = prodKeys && !prodKeys.has(key)
          return (
          <Link
            key={to}
            to={to}
            className={`nav-item${isActive(to) ? ' active' : ''}`}
            title={label}
          >
            <Icon size={17} className="nav-icon" />
            <span className="nav-label">{label}</span>
            {isDev && !collapsed && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                color: 'var(--ns-brand)', opacity: 0.8,
                background: 'color-mix(in oklch, var(--ns-brand) 12%, transparent)',
                borderRadius: 3, padding: '1px 4px', marginLeft: 'auto',
              }}>DEV</span>
            )}
          </Link>
          )
        })}

      </div>

      {/* Clementine section — Clementine role only */}
      {isClementine && (
        <>
          <div className="nav-section-label" style={{ marginTop: 12 }}>Clementine</div>
          <Link
            to="/clementine"
            className={`nav-item${isActive('/clementine') ? ' active' : ''}`}
            title="Clementine Host"
          >
            <Workflow size={17} className="nav-icon" />
            <span className="nav-label">Clementine Host</span>
          </Link>
        </>
      )}

      {/* Settings section — admin only */}
      {isAdmin && (
        <>
          <div className="nav-section-label" style={{ marginTop: 12 }}>Settings</div>
          {SETTINGS_NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className={`nav-item${isActive(to) ? ' active' : ''}`}
              title={label}
            >
              <Icon size={17} className="nav-icon" />
              <span className="nav-label">{label}</span>
            </Link>
          ))}
        </>
      )}

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--ns-border)', padding: '4px 0' }}>
        <button className="nav-item" style={{ width: '100%' }} onClick={toggleTheme} title="Toggle theme">
          {theme === 'dark'
            ? <Sun size={17} className="nav-icon" />
            : <Moon size={17} className="nav-icon" />}
          <span className="nav-label">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
        <button
          className="nav-item"
          style={{ width: '100%', color: 'var(--ns-err)' }}
          title="Sign out"
          onClick={() => instance.logoutRedirect({ postLogoutRedirectUri: '/' })}
        >
          <LogOut size={17} className="nav-icon" />
          <span className="nav-label">Sign out</span>
        </button>
      </div>

      {!collapsed && (
        <div style={{ padding: 10, borderTop: '1px solid var(--ns-border)' }}>
          <div style={{
            background: 'var(--ns-surface-2)',
            border: '1px solid var(--ns-border)',
            borderRadius: 8,
            padding: 10,
          }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <Lock size={12} style={{ color: 'var(--ns-text-muted)' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Signed in via Entra
              </span>
            </div>
            <div className="row">
              <div className="avatar">{initials}</div>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {user?.name ?? user?.username}
                </div>
                <div className="muted" style={{ fontSize: 11 }}>Admin</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

function getInitials(name: string) {
  return name.split(/[\s@]/).filter(Boolean).map(p => p[0]).join('').toUpperCase().slice(0, 2)
}
